import { Callout, css, Modal, PrimaryActionProps, styled } from 'canoe';
import { createCacheKey, ENDPOINTS, fetchResource, useCreatePayload } from 'hammock';
import React, { useState } from 'react';
import { useQuery } from 'react-query';

import { ContentMetadata, ContentType } from '@candu-ai/content/types';
import { isTooltipContent } from 'craft/constants';
import { TableLink } from 'dashboard/components/TableLinks';

import { DocumentPreview } from '../DocumentPreview';

const ThumbnailPreviewContainer = styled.div`
  ${({ theme }) => css`
    width: 270px;
    height: 180px;
    padding: ${theme.spaces.xs};
    background: ${theme.colors.White};
    border-radius: ${theme.borderRadius.small};
    border: 1px solid ${theme.colors.GlitterGray};
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  `}
`;

const ThumbnailPreviewTransform = styled.div`
  ${({ theme }) => css`
    width: 1260px;
    height: 800px;
    position: absolute;
    left: ${theme.spaces.xs};
    top: ${theme.spaces.xs};
    transform: scale(0.2) translate(-200%, -200%);
    overflow: hidden;
  `}
`;

const ModalPreviewContainer = styled.div`
  height: 60vh;
  position: relative;
`;

const ModalPreviewTransform = styled.div`
  position: absolute;
  max-height: 100%;
  overflow: hidden;
`;

interface TableLinkWithContentPreviewProps {
  text: string;
  link: string;
  contentId?: string;
  contentResource?: ContentMetadata;
  openInModal?: boolean;
  modalAction?: PrimaryActionProps;
  applicationId?: number;
}

export const TableLinkWithContentPreview = React.memo(
  ({
    text,
    contentResource,
    contentId,
    openInModal,
    modalAction,
    applicationId,
    link,
  }: TableLinkWithContentPreviewProps) => {
    const [triggerRef, setTriggerRef] = useState<HTMLDivElement | null>(null);
    const [showPreview, setShowPreview] = useState<boolean>(false);

    const { get } = useCreatePayload(ENDPOINTS.contentMetadata);

    const payload = get({ id: contentId as string });
    const { data } = useQuery(
      createCacheKey(payload),
      () => fetchResource<ContentMetadata>(payload),
      {
        enabled: !contentResource && !!contentId && (!openInModal || showPreview),
      },
    );

    const previewedData = data || contentResource;

    const openPreview = () => setShowPreview(true);
    const closePreview = () => setShowPreview(false);

    if (!previewedData || previewedData.archivedAt) {
      return <>{text || previewedData?.name}</>;
    }

    if (!applicationId || !previewedData.defaultDocumentId) {
      return <TableLink text={text || previewedData.name} link={link} />;
    }

    if (openInModal) {
      return (
        <>
          <TableLink text={text || previewedData.name} onClick={openPreview} />
          {showPreview ? (
            <Modal
              closeOnClickOutside
              onClose={closePreview}
              data-cy="archived-preview-modal"
              modalWidth="large"
            >
              <Modal.Header title={previewedData.name} />
              <Modal.Body>
                <ModalPreviewContainer>
                  <ModalPreviewTransform>
                    {/* TODO: look into getting a version id for the document here */}
                    <DocumentPreview
                      applicationId={applicationId}
                      documentId={previewedData.defaultDocumentId}
                      checkeredBackdrop={
                        isTooltipContent(previewedData.contentType) ||
                        previewedData.contentType === ContentType.Modal
                      }
                    />
                  </ModalPreviewTransform>
                </ModalPreviewContainer>
              </Modal.Body>
              {modalAction ? <Modal.Footer primaryAction={modalAction} /> : null}
            </Modal>
          ) : null}
        </>
      );
    }

    return (
      <>
        {showPreview ? (
          <Callout referenceElement={triggerRef as HTMLDivElement} placement="right">
            {({ ref, style, attributes }) => (
              <div ref={ref} style={style} {...attributes}>
                <ThumbnailPreviewContainer>
                  <ThumbnailPreviewTransform>
                    {/* TODO: look into getting a version id for the document here */}
                    <DocumentPreview
                      applicationId={applicationId}
                      documentId={previewedData?.defaultDocumentId}
                      checkeredBackdrop={
                        isTooltipContent(previewedData.contentType) ||
                        previewedData.contentType === ContentType.Modal
                      }
                    />
                  </ThumbnailPreviewTransform>
                </ThumbnailPreviewContainer>
              </div>
            )}
          </Callout>
        ) : null}
        <TableLink
          setReferenceElement={setTriggerRef}
          onMouseEnter={openPreview}
          onMouseLeave={closePreview}
          text={text}
          link={link}
        />
      </>
    );
  },
);
